import { Box, Divider, Grid } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { format } from "date-fns";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CrossSkillHeader from "../../../../ts/components/cross-skilling/ui/cross-skill-header";
import WorkerSkillDetailsDialog from "../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/worker-skill-details-dialog";
import { useSkillMatrixFilters } from "../../../../ts/components/cross-skilling-dashboard/controllers/use-skill-matrix-filters";
import AppliedFilters from "../../../../ts/components/filters/applied-filters";
// Import PNG images for export
import boxEmptyPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/box_empty.png";
import boxFullPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/box_full.png";
import boxOnePng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/box_one.png";
import boxThreePng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/box_three.png";
import boxTwoPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/box_two.png";
import circleEmptyPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/circle_empty.png";
import circleFullPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/circle_full.png";
import circleHalfPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/circle_half.png";
import circleQuarterPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/circle_quarter.png";
import circleThreeQuartersPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/circle_three_quarters.png";
import donutEmptyPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/donut_empty.png";
import donutFullPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/donut_full.png";
import donutHalfPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/donut_half.png";
import donutQuarterPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/donut_quarter.png";
import donutThreeQuartersPng from "../../../../ts/design/skill-representaion-cell/assets/png-for-export/donut_three_quarters.png";
import useCrossSkillController from "../controllers/use-cross-skill-controller";
import CrossSkillingTable from "./cross-skill-table";

const ManageCrossSkilling = () => {
  const { t } = useTranslation();
  const isSkillDetailsDialogOpen = useSelector(
    (state) => state.manageWorkers?.isSkillDetailsDialogOpen
  );

  const [
    {
      containerBox,
      data,
      allData,
      filters,
      allJobRoles,
      allSkills,
      allSkillLevels,
      notAssignedSkillLevel,
      allWorkers,
      allOrganizations,
      allOrganizationTypes,
      allManagers,
      allLocations,
      filterStatus,
      selectedWorkCenter,
      selectedLocation,
      allWorkCenters,
      allShiftPatterns,
      updateWorkerSkillLevelStatus,
      workerTypes,
      dataProcessed,
      fetchManageWorkersDataStatus,
      fetchAllLocationsStatus,
      fetchAllManagersStatus,
      fetchAllOrganizationsStatus,
      fetchSkillLevelsStatus,
      fetchJobsByUserIdStatus,
      searchText,
      filterControllerEvents,
      filterControllerProps,
      filteredJobs,
      selectedColumns,
      columns,
      finalPickerColumn,
      skillMatrixFiltersStatus,
      skillMatrixAppliedFilters,
      totalWorkers,
      pageSize,
      selectedPage,
      skillInsightsEnabled,
      isSkillPriorityEnabled,
      skillPriorities,
      allJobs,
    },
    {
      setFilters,
      setData,
      setFilterStatus,
      dispatch,
      resetSkillLevelStatus,
      setSelectedWorkCenter,
      setSelectedLocation,
      setSearchText,
      handleSelectColumns,
      getFilter,
      setSelectedPage,
      setPageSize,
    },
  ] = useCrossSkillController();

  const {
    appliedFilters,
    setFilterOpen,
    filters: allFilters,
  } = useSkillMatrixFilters();

  const [isExporting, setIsExporting] = useState(false);
  const [exportableRowCount, setExportableRowCount] = useState(0);
  const getExportDataRef = useRef(null);

  const isTooDark = (color) => {
    let r, g, b;
    // Check the format of the color, HEX or RGB?
    if (color.match(/^rgb/)) {
      const match = color.match(
        /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
      );

      if (match) {
        r = parseInt(match[1]);
        g = parseInt(match[2]);
        b = parseInt(match[3]);
      } else {
        return false;
      }
    } else {
      // If RGB --> Convert it to HEX
      const shouldReplace = color.length < 5;
      let hexColor = 0;

      if (shouldReplace) {
        hexColor = +("0x" + color.slice(1).replace(/./g, "$&$&"));
      } else {
        hexColor = +("0x" + color.slice(1));
      }

      r = hexColor >> 16;
      g = (hexColor >> 8) & 255;
      b = hexColor & 255;
    }

    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

    // Using the HSP value, determine whether the color is light or dark
    return hsp < 139.5;
  };

  // Helper function to get PNG image for skill representation
  const getSkillRepresentationImage = (repCode) => {
    if (!repCode) return null;

    const codeArr = repCode.split("-");
    const repType = codeArr[0]; // donut, circle, box, alphabet, number
    const repLevel =
      codeArr.length > 2
        ? `${codeArr[1]}-${codeArr[2]}` // three-quarter -> three_quarter
        : codeArr[1]; // empty, quarter, half, full

    // Map for image imports
    const imageMap = {
      box: {
        empty: boxEmptyPng,
        quarter: boxOnePng,
        half: boxTwoPng,
        "three-quarter": boxThreePng,
        full: boxFullPng,
      },
      circle: {
        empty: circleEmptyPng,
        quarter: circleQuarterPng,
        half: circleHalfPng,
        "three-quarter": circleThreeQuartersPng,
        full: circleFullPng,
      },
      donut: {
        empty: donutEmptyPng,
        quarter: donutQuarterPng,
        half: donutHalfPng,
        "three-quarter": donutThreeQuartersPng,
        full: donutFullPng,
      },
    };

    return imageMap[repType]?.[repLevel] || null;
  };

  const handleExportDataSetter = useCallback((getDataFn) => {
    getExportDataRef.current = getDataFn;
  }, []);

  const handleRowCountChange = useCallback((count) => {
    setExportableRowCount(count);
  }, []);

  const handleExport = useCallback(() => {
    if (getExportDataRef.current) {
      const { rows, skills, columnGrouping } = getExportDataRef.current();
      setIsExporting({
        filteredRows: rows,
        visibleSkills: skills,
        columnGrouping: columnGrouping,
      });
    }
  }, []);

  useEffect(() => {
    if (
      isExporting &&
      isExporting.filteredRows &&
      isExporting.filteredRows.length > 0 &&
      allSkillLevels &&
      allSkillLevels.length > 0
    ) {
      const exportToExcel = async () => {
        try {
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet(t("cross-skilling_skills-matrix"));

          // Create a separate worksheet for filters
          const filtersWorksheet = workbook.addWorksheet(
            t("cross-skilling_applied-filters")
          );

          // Get column grouping data
          const columnGrouping = isExporting.columnGrouping || [];
          const hasColumnGrouping = columnGrouping.length > 0;

          // Helper function to add image to workbook and get imageId
          const addImageToWorkbook = async (imagePath) => {
            if (!imagePath) return null;
            try {
              const response = await fetch(imagePath);
              const blob = await response.blob();
              const buffer = await blob.arrayBuffer();
              const base64 = btoa(
                new Uint8Array(buffer).reduce(
                  (data, byte) => data + String.fromCharCode(byte),
                  ""
                )
              );
              return workbook.addImage({
                base64,
                extension: "png",
              });
            } catch (error) {
              console.error("Error loading image:", error);
              return null;
            }
          };

          // Pre-load all images and create a map of repCode to imageId
          const imageCache = {};
          for (const level of allSkillLevels) {
            const imagePath = getSkillRepresentationImage(level.repCode);
            if (imagePath && !imageCache[level.repCode]) {
              const imageId = await addImageToWorkbook(imagePath);
              if (imageId !== null) {
                imageCache[level.repCode] = imageId;
              }
            }
          }

          // --- 1. Define Styles ---
          const border = {
            top: { style: "thin", color: { argb: "FFE5E5E5" } },
            left: { style: "thin", color: { argb: "FFE5E5E5" } },
            bottom: { style: "thin", color: { argb: "FFE5E5E5" } },
            right: { style: "thin", color: { argb: "FFE5E5E5" } },
          };

          const legendHeaderStyle = {
            font: { name: "Roboto", size: 11, bold: true },
            alignment: { vertical: "middle", horizontal: "left" },
          };

          const legendCellStyle = {
            font: { name: "Roboto", size: 10 },
            alignment: { vertical: "middle", horizontal: "center" },
            border,
          };

          const legendTextStyle = {
            font: { name: "Roboto", size: 10 },
            alignment: { vertical: "middle", horizontal: "left" },
            border,
          };

          const skillHeaderStyle = {
            fill: {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFAFAFA" },
            },
            font: { name: "Roboto", size: 10, bold: true },
            alignment: {
              vertical: "middle",
              horizontal: "center",
              wrapText: true,
            },
            border,
          };

          const workerHeaderStyle = {
            fill: {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFAFAFA" },
            },
            font: {
              name: "Roboto",
              size: 12,
              bold: true,
              color: { argb: "FF000000" },
            },
            alignment: { vertical: "middle", horizontal: "left" },
            border,
          };

          const workerCellStyle = {
            font: { name: "Roboto", size: 10 },
            alignment: {
              vertical: "middle",
              horizontal: "left",
              wrapText: true,
            },
            border,
          };

          const skillCellStyle = {
            font: { name: "Roboto", size: 10 },
            alignment: {
              vertical: "middle",
              horizontal: "center",
              wrapText: true,
            },
            border,
          };

          // --- 2. Use filtered data (this is the data already filtered by the table) ---
          const filteredWorkers = [...isExporting.filteredRows].sort((a, b) => {
            const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
            const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
            return nameA.localeCompare(nameB);
          });
          const visibleSkills = isExporting.visibleSkills || [];

          // --- 3. Build Skill Level Legend ---
          let currentRow = 1;
          const legendHeaderRow = worksheet.getRow(currentRow++);
          legendHeaderRow.getCell("A").value = t(
            "cross-skilling_skill-level-legend"
          );
          legendHeaderRow.getCell("A").style = legendHeaderStyle;
          legendHeaderRow.height = 20;

          allSkillLevels.forEach((level) => {
            const row = worksheet.getRow(currentRow);
            const imageId = imageCache[level.repCode];

            if (imageId !== undefined && imageId !== null) {
              // Use image for box, circle, and donut representations
              row.getCell("A").value = "";
              row.getCell("A").style = {
                ...legendCellStyle,
                fill: {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: {
                    argb: level.backColorCode?.replace("#", "FF") || "FFFFFFFF",
                  },
                },
              };

              // Add image to cell - centered using decimal offset
              // Image size is 20x20, cell height is 25, so offset to center
              worksheet.addImage(imageId, {
                tl: { col: 0.8, row: currentRow - 1 + 0.15 },
                ext: { width: 90, height: 40 },
                editAs: "oneCell",
              });
            } else {
              // No image available - use level value as fallback
              row.getCell("A").value = level.value?.toUpperCase() || "";
              row.getCell("A").style = {
                ...legendCellStyle,
                fill: {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: {
                    argb: level.backColorCode?.replace("#", "FF") || "FFFFFFFF",
                  },
                },
                font: {
                  ...legendCellStyle.font,
                  color: {
                    argb: level.foreColorCode?.replace("#", "FF") || "FF000000",
                  },
                },
              };
            }

            row.getCell("B").value = level.name || level.desc;
            row.getCell("B").style = legendTextStyle;
            row.height = 25;
            currentRow++;
          });

          currentRow++; // Empty row after legend

          // --- Populate Applied Filters Worksheet ---
          // Filter out empty filters (filters with no values)
          const nonEmptyFilters =
            allFilters?.filter((filter) => {
              const { type, ...rest } = filter;
              const valuesArray = Object.values(rest)[0] || [];
              return Array.isArray(valuesArray) && valuesArray.length > 0;
            }) || [];

          let filtersRow = 1;

          // Add header to filters sheet
          const filtersHeaderRow = filtersWorksheet.getRow(filtersRow++);
          filtersHeaderRow.getCell("A").value = t(
            "cross-skilling_applied-filters"
          );
          filtersHeaderRow.getCell("A").style = {
            font: { name: "Roboto", size: 14, bold: true },
            alignment: { vertical: "middle", horizontal: "left" },
          };
          filtersHeaderRow.height = 25;

          // Set column widths for filter display
          filtersWorksheet.getColumn("A").width = 30;
          filtersWorksheet.getColumn("B").width = 80;

          filtersRow++; // Empty row

          if (nonEmptyFilters.length > 0) {
            // Add column headers
            const columnHeaderRow = filtersWorksheet.getRow(filtersRow++);
            columnHeaderRow.getCell("A").value = t("cross-skilling_filter-type");
            columnHeaderRow.getCell("B").value = t(
              "cross-skilling_selected-values"
            );
            columnHeaderRow.eachCell((cell) => {
              cell.style = {
                font: { name: "Roboto", size: 11, bold: true },
                alignment: { vertical: "middle", horizontal: "left" },
                fill: {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFFAFAFA" },
                },
                border,
              };
            });
            columnHeaderRow.height = 20;

            nonEmptyFilters.forEach((filter) => {
              // extract type and the rest (there should be exactly one other key like 'locations' or 'workCenters')
              const { type, ...rest } = filter;

              // pull the first property value from rest (the array we care about), or [] if missing
              const valuesArray = Object.values(rest)[0] || [];

              // map to names (fall back to stringified object if name not present)
              const names = Array.isArray(valuesArray)
                ? valuesArray
                    .map((v) =>
                      v && v.name != null ? String(v.name) : JSON.stringify(v)
                    )
                    .join(", ")
                : String(valuesArray);

              // Use getRow to track filtersRow properly
              const row = filtersWorksheet.getRow(filtersRow++);
              row.getCell("A").value = type;
              row.getCell("B").value = names;

              row.eachCell((cell) => {
                cell.style = {
                  font: { name: "Roboto", size: 10 },
                  alignment: {
                    vertical: "middle",
                    horizontal: "left",
                    wrapText: true,
                  },
                  border,
                };
              });

              // Layout tweaks
              row.height = 20;
            });
          } else {
            // No filters applied
            const noFiltersRow = filtersWorksheet.getRow(filtersRow++);
            noFiltersRow.getCell("A").value = t(
              "cross-skilling_no-filters-applied"
            );
            noFiltersRow.getCell("A").style = {
              font: { name: "Roboto", size: 10, italic: true },
              alignment: { vertical: "middle", horizontal: "left" },
            };
            noFiltersRow.height = 20;
          }

          // --- 5. Build header rows based on selected columns and visible skills ---
          // Map of column field to display name
          const COLUMN_DISPLAY_NAMES = {
            companyEmployeeId: t("cross-skilling_worker-id"),
            name: t("cross-skilling_worker-name"),
            jobTitle: t("cross-skilling_job-title"),
            workerType: t("cross-skilling_worker-type"),
            workerSubTypeText: t("cross-skilling_worker-sub-type"),
            primaryJob: t("cross-skilling_primary-job"),
            managerName: t("cross-skilling_reporting-manager"),
            workCenter: t("cross-skilling_work-center"),
            location: t("cross-skilling_worker-location"),
            shiftPatternName: t("cross-skilling_shift-pattern"),
            joiningDate: t("cross-skilling_joining-date"),
            skillScore: t("cross-skilling_total-skill-score"),
          };

          let colIndex = 1;
          const staticColumnMap = [];
          const skillColumns = [];

          // Check if we have priority grouping (3-level hierarchy)
          // We need to check if ANY job group has priority children (nested structure)
          const hasPriorityGrouping =
            hasColumnGrouping &&
            columnGrouping.length > 0 &&
            columnGrouping.some(
              (group) =>
                group.children &&
                group.children.length > 0 &&
                group.children.some(
                  (child) => child.children && child.children.length > 0
                )
            );

          // Create header rows based on grouping structure
          let jobHeaderRow;
          let priorityHeaderRow;
          let skillHeaderRow;

          if (hasPriorityGrouping) {
            // 3-level hierarchy: Job > Priority > Skill
            jobHeaderRow = worksheet.getRow(currentRow++);
            jobHeaderRow.height = 50;
            priorityHeaderRow = worksheet.getRow(currentRow++);
            priorityHeaderRow.height = 50;
            skillHeaderRow = worksheet.getRow(currentRow++);
            skillHeaderRow.height = 50;
          } else if (hasColumnGrouping) {
            // 2-level hierarchy: Job > Skill
            jobHeaderRow = worksheet.getRow(currentRow++);
            jobHeaderRow.height = 50;
            skillHeaderRow = worksheet.getRow(currentRow++);
            skillHeaderRow.height = 50;
          } else {
            // 1-level: Just skills
            skillHeaderRow = worksheet.getRow(currentRow++);
            skillHeaderRow.height = 50;
          }

          // --- Add only selected static columns ---
          selectedColumns.forEach((field) => {
            // Skip skillScore column if skill priority is not enabled
            if (field === "skillScore" && !isSkillPriorityEnabled) {
              return;
            }
            if (field in COLUMN_DISPLAY_NAMES) {
              // For static columns, merge cells across all header rows
              if (hasPriorityGrouping) {
                // Merge from job row to skill row (3 rows)
                worksheet.mergeCells(
                  jobHeaderRow.number,
                  colIndex,
                  skillHeaderRow.number,
                  colIndex
                );
                jobHeaderRow.getCell(colIndex).value =
                  COLUMN_DISPLAY_NAMES[field];
                jobHeaderRow.getCell(colIndex).style = workerHeaderStyle;
              } else if (hasColumnGrouping) {
                // Merge from job row to skill row (2 rows)
                worksheet.mergeCells(
                  jobHeaderRow.number,
                  colIndex,
                  skillHeaderRow.number,
                  colIndex
                );
                jobHeaderRow.getCell(colIndex).value =
                  COLUMN_DISPLAY_NAMES[field];
                jobHeaderRow.getCell(colIndex).style = workerHeaderStyle;
              } else {
                // No grouping - just one row
                skillHeaderRow.getCell(colIndex).value =
                  COLUMN_DISPLAY_NAMES[field];
                skillHeaderRow.getCell(colIndex).style = workerHeaderStyle;
              }

              worksheet.getColumn(colIndex).width = field === "name" ? 25 : 20;

              staticColumnMap.push({
                colIndex,
                field,
              });

              colIndex++;
            }
          });

          // --- Build skill columns with job grouping ---
          if (hasPriorityGrouping) {
            // 3-level hierarchy: Job > Priority > Skill
            columnGrouping.forEach((jobGroup) => {
              const jobStartCol = colIndex;

              // Handle jobs with no mapped skills
              if (jobGroup.noSkill) {
                // Add "No Skill" placeholder column
                skillHeaderRow.getCell(colIndex).value =
                  t("cross-skilling_no-skill");
                skillHeaderRow.getCell(colIndex).style = skillHeaderStyle;
                worksheet.getColumn(colIndex).width = 12;

                // Leave priority row empty for this column
                priorityHeaderRow.getCell(colIndex).value = "";
                priorityHeaderRow.getCell(colIndex).style = skillHeaderStyle;

                skillColumns.push({
                  colIndex,
                  jobId: parseInt(jobGroup.groupId),
                  skillId: -2,
                });

                colIndex++;
              } else {
                // Skip if no children
                if (!jobGroup.children || jobGroup.children.length === 0)
                  return;

                // Process each priority within this job
                jobGroup.children.forEach((priorityGroup) => {
                  const priorityStartCol = colIndex;

                  // Skip if no children
                  if (
                    !priorityGroup.children ||
                    priorityGroup.children.length === 0
                  )
                    return;

                  // Process each skill within this priority
                  priorityGroup.children.forEach((skillField) => {
                    // Extract skillId and jobId from field (format: "skillId-jobId")
                    const fieldParts = skillField.field.split("-");
                    const skillId = parseInt(fieldParts[0]);
                    const jobId = parseInt(fieldParts[1]) || -1;

                    // Find the skill in visibleSkills
                    const skill = visibleSkills.find(
                      (s) =>
                        (s.skillId === skillId || s.id === skillId) &&
                        (s.jobId === jobId ||
                          (jobId === -1 &&
                            (s.jobId === -1 || s.jobId === null)))
                    );

                    if (skill) {
                      skillHeaderRow.getCell(colIndex).value =
                        skill.skillName || skill.name;
                      skillHeaderRow.getCell(colIndex).style = skillHeaderStyle;
                      worksheet.getColumn(colIndex).width = 12;

                      skillColumns.push({
                        colIndex,
                        jobId: skill.jobId || -1,
                        skillId: skill.skillId || skill.id,
                        priorityId: skill.priorityId,
                      });

                      colIndex++;
                    }
                  });

                  // Add priority header spanning all skills in this priority
                  const priorityEndCol = colIndex - 1;
                  if (priorityStartCol <= priorityEndCol) {
                    // Find priority info from skillPriorities
                    // Try multiple matching strategies
                    const priorityGroupIdClean = priorityGroup.groupId.replace(
                      "weight--",
                      ""
                    );
                    const priority = skillPriorities?.find(
                      (p) =>
                        p.id.toString() === priorityGroupIdClean ||
                        p.id.toString() === priorityGroup.groupId ||
                        p.name === priorityGroup.headerName
                    );

                    // Use priority info if found, otherwise use defaults from priorityGroup
                    const priorityColorCode = priority?.hexColor || "EBEBEB";
                    const priorityText =
                      priority?.code ||
                      priority?.name ||
                      priorityGroup.headerName ||
                      "";
                    const backgroundColor = `#${priorityColorCode}`;
                    const textColor = isTooDark(backgroundColor)
                      ? "FFFFFFFF"
                      : "FF000000";

                    const priorityHeaderStyle = {
                      fill: {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: priorityColorCode.replace("#", "FF") },
                      },
                      font: {
                        name: "Roboto",
                        size: 11,
                        bold: true,
                        color: { argb: textColor },
                      },
                      alignment: {
                        vertical: "middle",
                        horizontal: "center",
                        wrapText: true,
                      },
                      border,
                    };

                    if (priorityStartCol === priorityEndCol) {
                      priorityHeaderRow.getCell(priorityStartCol).value =
                        priorityText.toUpperCase();
                      priorityHeaderRow.getCell(priorityStartCol).style =
                        priorityHeaderStyle;
                    } else {
                      worksheet.mergeCells(
                        priorityHeaderRow.number,
                        priorityStartCol,
                        priorityHeaderRow.number,
                        priorityEndCol
                      );
                      priorityHeaderRow.getCell(priorityStartCol).value =
                        priorityText.toUpperCase();
                      priorityHeaderRow.getCell(priorityStartCol).style =
                        priorityHeaderStyle;
                    }
                  }
                });
              }

              // Add job header spanning all priorities/skills in this job
              const jobEndCol = colIndex - 1;
              if (jobStartCol <= jobEndCol) {
                // Get job color from the group or from skills
                const jobColorCode =
                  jobGroup.jobColor ||
                  visibleSkills.find(
                    (s) =>
                      s.jobId?.toString() === jobGroup.groupId ||
                      (jobGroup.groupId === "-1" &&
                        (s.jobId === -1 || s.jobId === null))
                  )?.jobColor ||
                  "EBEBEB";
                const backgroundColor = `#${jobColorCode}`;
                const textColor = isTooDark(backgroundColor)
                  ? "FFFFFFFF"
                  : "FF000000";

                const jobHeaderStyleWithColor = {
                  fill: {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: jobColorCode.replace("#", "FF") },
                  },
                  font: {
                    name: "Roboto",
                    size: 12,
                    bold: true,
                    color: { argb: textColor },
                  },
                  alignment: {
                    vertical: "middle",
                    horizontal: "center",
                    wrapText: true,
                  },
                  border,
                };

                if (jobStartCol === jobEndCol) {
                  jobHeaderRow.getCell(jobStartCol).value = jobGroup.headerName;
                  jobHeaderRow.getCell(jobStartCol).style =
                    jobHeaderStyleWithColor;
                } else {
                  worksheet.mergeCells(
                    jobHeaderRow.number,
                    jobStartCol,
                    jobHeaderRow.number,
                    jobEndCol
                  );
                  jobHeaderRow.getCell(jobStartCol).value = jobGroup.headerName;
                  jobHeaderRow.getCell(jobStartCol).style =
                    jobHeaderStyleWithColor;
                }
              }
            });
          } else if (hasColumnGrouping) {
            // 2-level hierarchy: Job > Skill (no priorities)
            columnGrouping.forEach((group) => {
              const startCol = colIndex;
              let groupSkillCount = 0;

              // Handle jobs with no mapped skills
              if (group.noSkill) {
                // Add "No Skill" placeholder column
                skillHeaderRow.getCell(colIndex).value =
                  t("cross-skilling_no-skill");
                skillHeaderRow.getCell(colIndex).style = skillHeaderStyle;
                worksheet.getColumn(colIndex).width = 12;

                skillColumns.push({
                  colIndex,
                  jobId: parseInt(group.groupId),
                  skillId: -2,
                });

                colIndex++;
                groupSkillCount++;
              } else {
                // Find all skills for this job group
                const groupSkills = visibleSkills.filter((skill) => {
                  const skillJobId = skill.jobId || -1;
                  const groupId = group.groupId;

                  // Handle different groupId formats
                  if (groupId === "-1") {
                    // Other Skills group
                    return skillJobId === -1 || skillJobId === null;
                  } else if (groupId === "000") {
                    // Legacy "Other Skills" group
                    return skillJobId === -1 || skillJobId === null;
                  } else {
                    // Regular job groups
                    return skillJobId.toString() === groupId.toString();
                  }
                });

                groupSkills.forEach((skill) => {
                  skillHeaderRow.getCell(colIndex).value =
                    skill.skillName || skill.name;
                  skillHeaderRow.getCell(colIndex).style = skillHeaderStyle;
                  worksheet.getColumn(colIndex).width = 12;

                  skillColumns.push({
                    colIndex,
                    jobId: skill.jobId || -1,
                    skillId: skill.skillId || skill.id,
                  });

                  colIndex++;
                  groupSkillCount++;
                });
              }

              // Add job header spanning all skills in this group
              if (groupSkillCount > 0) {
                const endCol = colIndex - 1;

                // Get job color from the group or default
                const jobColorCode = group.jobColor || "ebebeb";
                const backgroundColor = `#${jobColorCode}`;
                const textColor = isTooDark(backgroundColor)
                  ? "FFFFFFFF"
                  : "FF000000";

                // Create job header style with job color
                const jobHeaderStyleWithColor = {
                  fill: {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: jobColorCode.replace("#", "FF") },
                  },
                  font: {
                    name: "Roboto",
                    size: 12,
                    bold: true,
                    color: { argb: textColor },
                  },
                  alignment: {
                    vertical: "middle",
                    horizontal: "center",
                    wrapText: true,
                  },
                  border,
                };

                if (startCol === endCol) {
                  // Single column, no merge needed
                  jobHeaderRow.getCell(startCol).value = group.headerName;
                  jobHeaderRow.getCell(startCol).style =
                    jobHeaderStyleWithColor;
                } else {
                  // Multiple columns, merge them
                  worksheet.mergeCells(
                    jobHeaderRow.number,
                    startCol,
                    jobHeaderRow.number,
                    endCol
                  );
                  jobHeaderRow.getCell(startCol).value = group.headerName;
                  jobHeaderRow.getCell(startCol).style =
                    jobHeaderStyleWithColor;
                }
              }
            });
          } else {
            // No column grouping - just show skills without job headers
            visibleSkills.forEach((skill) => {
              skillHeaderRow.getCell(colIndex).value =
                skill.skillName || skill.name;
              skillHeaderRow.getCell(colIndex).style = skillHeaderStyle;
              worksheet.getColumn(colIndex).width = 12;

              skillColumns.push({
                colIndex,
                jobId: skill.jobId || -1,
                skillId: skill.skillId || skill.id,
              });

              colIndex++;
            });
          }

          // --- 6. Populate worker data rows ---
          filteredWorkers.forEach((worker) => {
            const dataRow = worksheet.getRow(currentRow++);

            // Worker static columns - only show selected ones
            staticColumnMap.forEach(({ colIndex, field }) => {
              let value = "-";

              if (field === "name") {
                value = `${worker.firstName} ${worker.lastName}`;
              } else if (field === "companyEmployeeId") {
                value = worker.companyEmployeeId || "-";
              } else if (field === "joiningDate") {
                value = worker.joiningDate
                  ? format(new Date(worker.joiningDate), "MM/dd/yyyy")
                  : "-";
              } else if (field in worker) {
                value = worker[field] || "-";
              }

              dataRow.getCell(colIndex).value = value;
              dataRow.getCell(colIndex).style = workerCellStyle;
            });

            // Worker skills - only show visible ones
            skillColumns.forEach(({ colIndex, jobId, skillId }) => {
              const workerSkill = worker.skills?.find(
                (ws) => ws.skillId === skillId
              );

              if (workerSkill) {
                const skillLevel = allSkillLevels.find(
                  (level) => level.id === workerSkill.levelId
                );

                if (skillLevel) {
                  const imageId = imageCache[skillLevel.repCode];

                  if (imageId !== undefined && imageId !== null) {
                    // Use image for box, circle, and donut representations
                    dataRow.getCell(colIndex).value = "";
                    dataRow.getCell(colIndex).style = {
                      ...skillCellStyle,
                      fill: {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: {
                          argb:
                            skillLevel.backColorCode?.replace("#", "FF") ||
                            "FFFFFFFF",
                        },
                      },
                    };

                    // Add image to cell - centered using decimal offset
                    worksheet.addImage(imageId, {
                      tl: {
                        col: colIndex - 1 + 0.25,
                        row: dataRow.number - 1 + 0.15,
                      },
                      ext: { width: 90, height: 40 },
                      editAs: "oneCell",
                    });
                  } else {
                    // No image available - use level value as fallback
                    dataRow.getCell(colIndex).value =
                      skillLevel.value?.toUpperCase() || "";
                    dataRow.getCell(colIndex).style = {
                      ...skillCellStyle,
                      fill: {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: {
                          argb:
                            skillLevel.backColorCode?.replace("#", "FF") ||
                            "FFFFFFFF",
                        },
                      },
                      font: {
                        ...skillCellStyle.font,
                        color: {
                          argb:
                            skillLevel.foreColorCode?.replace("#", "FF") ||
                            "FF000000",
                        },
                      },
                    };
                  }
                } else {
                  dataRow.getCell(colIndex).value = "";
                  dataRow.getCell(colIndex).style = skillCellStyle;
                }
              } else {
                // Check if worker has expressed interest in this skill
                const isInterested = worker.skillInterests?.find(
                  (si) =>
                    parseInt(String(si.skillId)) === parseInt(String(skillId))
                );

                if (isInterested) {
                  dataRow.getCell(colIndex).value = t(
                    "cross-skilling_interested"
                  );
                  dataRow.getCell(colIndex).style = {
                    ...skillCellStyle,
                    font: {
                      ...skillCellStyle.font,
                      size: 9,
                      color: { argb: "FF49454F" },
                    },
                  };
                } else {
                  dataRow.getCell(colIndex).value = "";
                  dataRow.getCell(colIndex).style = skillCellStyle;
                }
              }
            });

            dataRow.height = 25;
          });

          // --- 7. Generate and download file ---
          const buffer = await workbook.xlsx.writeBuffer();
          const fileName = `SkillsMatrix_${
            new Date().toISOString().split("T")[0]
          }.xlsx`;

          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          saveAs(blob, fileName);
        } catch (error) {
          console.error("Error during Excel export:", error);
        } finally {
          setIsExporting(false);
        }
      };

      exportToExcel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isExporting,
    allSkillLevels,
    allLocations,
    selectedLocation,
    selectedColumns,
    t,
  ]);

  try {
    return (
      <Box>
        <Grid ref={containerBox} container flexDirection={"column"}>
          <Grid item xs md lg>
            <CrossSkillHeader
              allSkillLevels={allSkillLevels}
              notAssignedSkillLevel={notAssignedSkillLevel}
              allJobRoles={allJobRoles}
              allSkills={allSkills}
              allData={allData}
              allLocations={allLocations}
              changeData={(data) => setData(data)}
              data={data}
              selectedWorkCenter={selectedWorkCenter}
              setSelectedWorkCenter={setSelectedWorkCenter}
              selectedLocation={selectedLocation}
              columns={finalPickerColumn}
              allWorkCenters={allWorkCenters}
              handleSelectColumns={handleSelectColumns}
              searchText={searchText}
              search={(value) => setSearchText(value)}
              fetchManageWorkersDataStatus={fetchManageWorkersDataStatus}
              fetchAllLocationsStatus={fetchAllLocationsStatus}
              fetchAllManagersStatus={fetchAllManagersStatus}
              fetchAllOrganizationsStatus={fetchAllOrganizationsStatus}
              fetchSkillLevelsStatus={fetchSkillLevelsStatus}
              fetchJobsByUserIdStatus={fetchJobsByUserIdStatus}
              skillMatrixFiltersStatus={skillMatrixFiltersStatus}
              isSkillPriorityEnabled={isSkillPriorityEnabled}
              handleExport={handleExport}
              hasDataToExport={exportableRowCount > 0}
              isDataLoading={!dataProcessed}
            />
            <Divider />
          </Grid>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container wrap="nowrap" spacing={0}>
              <Grid item xs lg md sm>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    flex: 1,
                  }}
                >
                  <AppliedFilters />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    flex: 1,
                  }}
                >
                  <CrossSkillingTable
                    data={data}
                    filters={filters}
                    appliedFilters={appliedFilters}
                    allJobs={filteredJobs}
                    allSkillLevels={allSkillLevels}
                    notAssignedSkillLevel={notAssignedSkillLevel}
                    allOrganizations={allOrganizations}
                    dataProcessed={dataProcessed}
                    fetchManageWorkersDataStatus={fetchManageWorkersDataStatus}
                    fetchAllLocationsStatus={fetchAllLocationsStatus}
                    selectedColumns={columns.filter((x) =>
                      selectedColumns.includes(x.field)
                    )}
                    searchText={searchText}
                    getFilter={getFilter}
                    allOrganizationTypes={allOrganizationTypes}
                    totalWorkers={totalWorkers}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    selectedPage={selectedPage}
                    setSelectedPage={setSelectedPage}
                    onExport={handleExportDataSetter}
                    onRowCountChange={handleRowCountChange}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        {updateWorkerSkillLevelStatus === "fulfilled" ? (
          <Snackbar
            open={updateWorkerSkillLevelStatus === "fulfilled"}
            autoHideDuration={3000}
            onClose={() => dispatch(resetSkillLevelStatus())}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="success">
              {t("cross-skilling_skill-level-updated")}
            </MuiAlert>
          </Snackbar>
        ) : null}

        {isSkillDetailsDialogOpen && <WorkerSkillDetailsDialog />}
      </Box>
    );
  } catch (e) {
    console.error(e);
    return <></>;
  }
};

export default ManageCrossSkilling;
